interface StoredCity {
  slug: string
  name: string
}

const LEGACY_SELECTED_SLUG = 'selectedCity'
const LEGACY_SELECTED_NAME = 'selectedCityName'

const CONFIRMED_SLUG = 'cityConfirmedSlug'
const CONFIRMED_NAME = 'cityConfirmedName'
const SUGGESTED_SLUG = 'citySuggestedSlug'
const SUGGESTED_NAME = 'citySuggestedName'

function getStoredValue(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function setStoredValue(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value)
    return true
  } catch {
    return false
  }
}

function removeStoredValue(key: string): void {
  try {
    localStorage.removeItem(key)
  } catch {
    return
  }
}

function migrateLegacyCityKeys(): void {
  if (!import.meta.client) {
    return
  }
  const hasNewConfirmed = Boolean(getStoredValue(CONFIRMED_SLUG))
  const legacySlug = getStoredValue(LEGACY_SELECTED_SLUG)

  if (hasNewConfirmed || !legacySlug) {
    return
  }

  const legacyName = getStoredValue(LEGACY_SELECTED_NAME) ?? legacySlug
  const nameSaved = setStoredValue(CONFIRMED_NAME, legacyName)
  const slugSaved = setStoredValue(CONFIRMED_SLUG, legacySlug)
  if (!nameSaved || !slugSaved) {
    return
  }

  removeStoredValue(LEGACY_SELECTED_SLUG)
  removeStoredValue(LEGACY_SELECTED_NAME)
}

function clearSuggestedKeys(): void {
  removeStoredValue(SUGGESTED_SLUG)
  removeStoredValue(SUGGESTED_NAME)
}

function clearConfirmedKeys(): void {
  removeStoredValue(CONFIRMED_SLUG)
  removeStoredValue(CONFIRMED_NAME)
}

function reconcileConfirmedOverSuggestedKeys(): void {
  if (!import.meta.client) {
    return
  }
  migrateLegacyCityKeys()
  if (getStoredValue(CONFIRMED_SLUG)) {
    clearSuggestedKeys()
  }
}

export const useCityStorage = () => {
  const readConfirmedSlug = (): string | null => {
    if (!import.meta.client) {
      return null
    }
    reconcileConfirmedOverSuggestedKeys()

    return getStoredValue(CONFIRMED_SLUG)
  }

  const readConfirmedCityData = (): StoredCity | null => {
    if (!import.meta.client) {
      return null
    }
    reconcileConfirmedOverSuggestedKeys()
    const slug = getStoredValue(CONFIRMED_SLUG)
    const name = getStoredValue(CONFIRMED_NAME)
    if (!slug) {
      return null
    }
    if (!name) {
      return { slug, name: slug }
    }

    return { slug, name }
  }

  const readSuggestedCityData = (): StoredCity | null => {
    if (!import.meta.client) {
      return null
    }
    reconcileConfirmedOverSuggestedKeys()
    const slug = getStoredValue(SUGGESTED_SLUG)
    const name = getStoredValue(SUGGESTED_NAME)
    if (!slug) {
      return null
    }
    if (!name) {
      return { slug, name: slug }
    }

    return { slug, name }
  }

  const getConfirmedCitySlug = (): string | null => readConfirmedSlug()

  const getSuggestedCityData = (): StoredCity | null => readSuggestedCityData()

  /** Slug только подтверждённого города — для middleware и общих случаев. */
  const getStoredCity = (): string | null => getConfirmedCitySlug()

  const getStoredCityData = (): StoredCity | null => readConfirmedCityData()

  const setSuggestedCity = (slug: string, name: string): void => {
    if (!import.meta.client) {
      return
    }
    setStoredValue(SUGGESTED_SLUG, slug)
    setStoredValue(SUGGESTED_NAME, name)
  }

  const setConfirmedCity = (slug: string, name?: string): void => {
    if (!import.meta.client) {
      return
    }
    clearSuggestedKeys()
    setStoredValue(CONFIRMED_SLUG, slug)
    if (name) {
      setStoredValue(CONFIRMED_NAME, name)
    } else {
      removeStoredValue(CONFIRMED_NAME)
    }
  }

  const setStoredCity = (slug: string, name?: string): void => {
    setConfirmedCity(slug, name)
  }

  const clearStoredCity = (): void => {
    if (!import.meta.client) {
      return
    }
    clearConfirmedKeys()
    clearSuggestedKeys()
    removeStoredValue(LEGACY_SELECTED_SLUG)
    removeStoredValue(LEGACY_SELECTED_NAME)
  }

  const applyStoredCity = () => {
    const route = useRoute()
    const slug = import.meta.client ? getConfirmedCitySlug() : null
    if (!slug || route.params.city) {
      return null
    }

    if (route.path.startsWith('/uslugi/') && route.params.segment) {
      return null
    }

    return slug
  }

  return {
    getConfirmedCitySlug,
    getSuggestedCityData,
    getStoredCity,
    getStoredCityData,
    setSuggestedCity,
    setStoredCity,
    clearStoredCity,
    applyStoredCity,
    reconcileConfirmedOverSuggestedKeys,
  }
}
